/**
 * Flow component placement policy (QP-150).
 *
 * The single, pure source of truth for "may component type X be placed on a page
 * of type Y?". Shared by the flow-editor add-component dialog (UX filtering) and
 * the server-side flow-definition validation (authoritative), so the two can
 * never disagree. PDF section-scope allow-lists mirror this matrix.
 *
 * Page types: global | goal | portfolio.
 * Page visibility: internal (advisor workflow) | public (client workflow).
 */

export type FlowPageType = "global" | "goal" | "portfolio";
export type FlowPageVisibility = "internal" | "public";

/**
 * Advisor-administered components — internal pages only, whatever the page type.
 *
 * QL-314 — these components have no public renderer and their decisions can
 * only be taken by the advisor. They still gate the PUBLIC workflow through the
 * engine's cross-workflow compliance block (the client sees "advisor review
 * required", never the component), which is why they must not be placeable on a
 * public page: a public placement would render an empty slot AND be counted a
 * second time by the traversal.
 */
const ADVISOR_ONLY_COMPONENTS = new Set<string>([
  "volatility_chart",
  "equity_share_chart",
  // QL-375 — the mandate check and the follow-up comparison: advisor decisions,
  // no public renderer; the client sees "advisor review required".
  "mandate",
  "mandate_comparison",
  // QL-287 — advisor-only by decision; the client flow renders nothing for them.
  "account_cash_overview",
  "cash_transfers",
]);

/** Whether a component type is advisor-administered (internal pages only). */
export function isAdvisorOnlyComponent(componentType: string): boolean {
  return ADVISOR_ONLY_COMPONENTS.has(componentType);
}

/**
 * QL-314 — component values that are a compliance DECISION taken for one
 * session: the advisor accepts or refuses a deviation on the numbers of that
 * session, and the record carries the hash of those numbers. A follow-up
 * copies the source goal's other component values (risk assessment and the
 * like) so the advisor does not re-enter them, but a decision must never come
 * along: an unchanged model selection reproduces the hash, and the old
 * acceptance would silently satisfy the new session's gate and print in its
 * PDF (PR 485 review). The sibling decisions on goal_portfolio rows are not
 * copied either.
 */
export const SESSION_DECISION_COMPONENT_TYPES: readonly string[] = [
  "volatility_chart",
  "equity_share_chart",
  // QL-375 — a mandate deviation is accepted on one session's combined weights.
  "mandate",
  "mandate_comparison",
];

/** Layout/formatting components — allowed on every page type. */
const FORMATTING_COMPONENTS = new Set<string>([
  "text_block",
  "spacer",
  "youtube_video",
  "iframe",
  "image",
  "embed",
  "modal",
  "file",
]);

/** Goal-level inputs/analytics — goal page only. */
const GOAL_ONLY_COMPONENTS = new Set<string>([
  "goal_input",
  "volatility_chart", // QL-314 — goal-scoped volatility line chart with compliance gate
  "equity_share_chart", // QL-313 — goal-scoped equity share stacked bar with compliance gate
  "mandate_comparison", // QL-375 — follow-up: the goal's current vs new mandate (group 8)
]);

/**
 * QL-375 — allowed on goal AND portfolio pages, never global. The `mandate`
 * check reads the goal's combined allocation on a goal page (where it carries
 * the gate) and one portfolio's own weights on a portfolio page (informational
 * under goal-level blocking, the gate itself under portfolio-level blocking).
 * A global page has no goal to resolve a mandate for.
 */
const GOAL_AND_PORTFOLIO_COMPONENTS = new Set<string>(["mandate"]);

/** Portfolio-level inputs/analytics — portfolio page only. */
const PORTFOLIO_ONLY_COMPONENTS = new Set<string>([
  "portfolio_input",
  "custom_portfolio",
  "portfolio_display",
  "portfolio_xray",
  "asset_allocation",
  "portfolio_forecast",
  "historical_return",
  // QP-179 — per-portfolio sustainability matching table + deviation decision.
  "sustainability_status",
  // QL-416 — existing-vs-new metrics + one-time transition costs of a followup
  // portfolio. Per-portfolio by nature (it diffs one frozen composition against
  // one proposal), so it has no instance-wide form.
  "cost_benefit",
]);

/** Client/instance-level widgets — global page only. */
const GLOBAL_ONLY_COMPONENTS = new Set<string>([
  "financial_situation",
  // QP-179 — sustainability gate + preferences (like financial_situation, but
  // also renders on the public flow).
  "sustainability",
  // QP-224 — client preference yes/no answers; asked once per instance, so
  // global pages only (renders on both advisor and public flow).
  "client_preferences",
  "wealth_plan",
  // QL-281 — instance-wide goal list (add / edit / delete goals); global pages
  // only, renders on both the advisor and the public flow.
  "goal_list",
  "trapets_lookup",
  "sumsub_verification",
  "enin_lookup",
  "client_classification",
  "product_assessment",
  // QL-13 — standalone knowledge quiz. Asked once per instance (its state is
  // keyed by quizId in a single instance-global component value row), so global
  // pages only. Renders on both advisor and public flow.
  "quiz",
]);

/**
 * Allowed on global and goal pages, never on portfolio pages.
 *
 * QL-212 — `risk_assessment` joined `portfolio_list` here: a risk model may
 * declare each of its three inputs as captured per goal or once per client, so
 * the component legitimately sits on a global page (client-level inputs), on a
 * goal page (goal-level inputs), or on one of each in the same flow. WHICH
 * sections a given placement actually shows is decided by `risk-input-scope`'s
 * `renderedRiskSections` — this matrix only says where the component may sit.
 * A portfolio page stays invalid: the assessment has no per-portfolio form.
 *
 * QL-315 — `asset_class_forecast` joined them, and is the first aggregate to be
 * allowed on a goal page. See AGGREGATE_ANALYTICS_COMPONENTS for why the others
 * are not: their goal-level aggregate would be ambiguous. This one's is not.
 * Its snapshot is computed at exactly two scopes, goal and instance, and goals
 * partition portfolios (`goal_portfolio.goalId` is a non-nullable scalar FK, no
 * M:N table), so "the mix across this goal's portfolios" has one unambiguous
 * reading and double-counts nothing. Portfolio pages are deliberately out of
 * scope for now — there is no portfolio-scoped row to read.
 */
const GLOBAL_AND_GOAL_COMPONENTS = new Set<string>([
  "portfolio_list",
  "risk_assessment",
  "asset_class_forecast",
  // QL-287 — the client's accounts and planned transfers. Instance-wide data
  // (every account, every transfer), so a goal page shows the same rows as a
  // global one and a second placement never double-counts.
  "account_cash_overview",
  "cash_transfers",
]);

/**
 * Aggregate analytics — allowed on global pages (whole-instance form) and on the
 * portfolio page (per-portfolio form), but never on goal pages (no ambiguous
 * goal-level aggregate).
 *
 * The goal-page exclusion is about these five specifically, not about aggregates
 * as a category: each sums money (cost, expected value) over a set the goal page
 * does not pin down, so a goal-level total would silently pick one reading. It
 * is not a blanket rule — `asset_class_forecast` (QL-315) is an aggregate that
 * does sit on goal pages, because its goal-scoped merge is well defined.
 */
const AGGREGATE_ANALYTICS_COMPONENTS = new Set<string>([
  "expected_value",
  "cost_table",
  "cost_chart",
  "portfolio_costs",
  "product_costs",
]);

/**
 * Every flow component type known to the placement matrix — the canonical
 * flow-component registry (includes `field`, which the matrix special-cases).
 */
export const ALL_FLOW_COMPONENT_TYPES: readonly string[] = [
  ...FORMATTING_COMPONENTS,
  "field",
  ...GOAL_ONLY_COMPONENTS,
  ...GOAL_AND_PORTFOLIO_COMPONENTS,
  ...PORTFOLIO_ONLY_COMPONENTS,
  ...GLOBAL_ONLY_COMPONENTS,
  ...GLOBAL_AND_GOAL_COMPONENTS,
  ...AGGREGATE_ANALYTICS_COMPONENTS,
].sort();

/**
 * Every PDF component type the PDF renderer handles — the canonical
 * PDF-component registry (raw `componentType` values as stored in
 * pdf_page_component, not the AI tool's `pdf_`-prefixed names).
 *
 * Single source of truth: the web `PdfComponentType` union
 * (apps/web/src/pages/settings/pdf-templates/detail/types.ts) is derived from
 * this list, so the two can never drift apart.
 */
export const ALL_PDF_COMPONENT_TYPES = [
  "advisor_image",
  "asset_allocation",
  "asset_class_forecast",
  "client_classification",
  "client_parties",
  "client_preferences",
  "cost_chart",
  "cost_table",
  "enin_lookup",
  "equity_share_chart",
  "expected_value",
  "financial_situation",
  "goal_info",
  "goal_list",
  "group_members",
  "heading",
  "historical_return",
  "image",
  // QL-375 group 10 — the mandate check and the follow-up comparison in the document.
  "mandate",
  "mandate_comparison",
  // QL-730 B2 — one portfolio's own mandate check, in a per-portfolio section.
  "mandate_portfolio",
  "page_break",
  "portfolio_costs",
  "portfolio_display",
  "portfolio_forecast",
  "portfolio_xray",
  "product_assessment",
  "product_costs",
  // QL-505 — the standalone knowledge quiz's PDF block.
  "quiz_results",
  "risk_assessment",
  "spacer",
  "sumsub_verification",
  "sustainability_preferences",
  "sustainability_status",
  "table",
  "table_of_contents",
  "text",
  "trapets_lookup",
  "volatility_chart",
  "wealth_plan",
] as const;

/**
 * Whether a component type may be placed on a page of the given type.
 *
 * Deny-by-default: an unrecognized component type returns `false`, so a new
 * component must be added to the matrix to be placeable anywhere.
 *
 * `visibility` is optional for callers that only know the page type (PDF
 * scope allow-lists, runtime renderers). When given, advisor-only components
 * are refused on public pages regardless of page type.
 */
export function isComponentAllowedOnPage(
  componentType: string,
  pageType: FlowPageType,
  visibility?: FlowPageVisibility,
): boolean {
  // QL-314 — advisor-administered components never sit on a public page.
  if (visibility === "public" && ADVISOR_ONLY_COMPONENTS.has(componentType)) return false;

  // Formatting is allowed everywhere.
  if (FORMATTING_COMPONENTS.has(componentType)) return true;

  // Custom `field` components: allowed on all page types. Per-field scope (global/goal/portfolio)
  // restricts which scope is valid on which page type (see isFieldAllowedOnPage).
  if (componentType === "field") return true;

  // Aggregate analytics: global (instance form) or portfolio (per-portfolio form).
  if (AGGREGATE_ANALYTICS_COMPONENTS.has(componentType)) {
    return pageType === "global" || pageType === "portfolio";
  }

  // Navigation/overview: global or goal (not portfolio).
  if (GLOBAL_AND_GOAL_COMPONENTS.has(componentType)) {
    return pageType === "global" || pageType === "goal";
  }

  if (GOAL_ONLY_COMPONENTS.has(componentType)) return pageType === "goal";
  // QL-375 — goal or portfolio, never global.
  if (GOAL_AND_PORTFOLIO_COMPONENTS.has(componentType)) {
    return pageType === "goal" || pageType === "portfolio";
  }
  if (PORTFOLIO_ONLY_COMPONENTS.has(componentType)) return pageType === "portfolio";
  if (GLOBAL_ONLY_COMPONENTS.has(componentType)) return pageType === "global";

  return false;
}
